import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { 
  BarChart3, 
  Crown, 
  PiggyBank, 
  Gift, 
  FileText, 
  TrendingUp,
  Smartphone,
  User,
  Shield,
  Palette,
  Settings,
  HelpCircle,
  CreditCard,
  Bell,
  LogOut,
  Wallet,
  Download,
  ShoppingCart,
  MessageCircle
} from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { usePWAInstall } from "@/hooks/use-pwa-install";
import { useTotalUnreadCount } from "@/hooks/useTotalUnreadCount";
import { Badge } from "@/components/ui/badge";

interface MoreMenuSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

interface MenuItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  route?: string;
  action?: () => void;
  color: string;
  badge?: number;
}

interface MenuSection {
  title: string;
  items: MenuItem[];
}

export function MoreMenuSheet({ isOpen, onClose }: MoreMenuSheetProps) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isMobile, isInstalled, isStandalone, promptInstall, canPromptDirectly } = usePWAInstall();
  const { unreadCount } = useTotalUnreadCount();

  const handleLogout = async () => {
    onClose();
    await supabase.auth.signOut();
    navigate("/auth");
  };

  const handleInstallApp = async () => {
    if (canPromptDirectly) {
      await promptInstall();
    } else {
      navigate('/install');
      onClose();
    }
  };

  // Check if we should show install option
  const showInstallOption = isMobile && !isInstalled && !isStandalone;

  const menuSections: MenuSection[] = [
    {
      title: t('moreMenu.sections.services'),
      items: [
        { id: "chat", label: t('moreMenu.items.chat'), icon: MessageCircle, route: "/chat-inbox", color: "bg-sky-500", badge: unreadCount > 0 ? unreadCount : undefined },
        { id: "analytics", label: t('moreMenu.items.financialAnalytics'), icon: BarChart3, route: "/analytics", color: "bg-blue-500" },
        { id: "vip", label: t('moreMenu.items.vipLevels'), icon: Crown, route: "/vip-levels", color: "bg-amber-500" },
        { id: "budget", label: t('moreMenu.items.budgeting'), icon: PiggyBank, route: "/budgeting-tools", color: "bg-green-500" },
        { id: "promo", label: t('moreMenu.items.promotions'), icon: Gift, route: "/mobile-promotions", color: "bg-pink-500" },
      ]
    },
    {
      title: t('moreMenu.sections.account'),
      items: [
        { id: "wallets", label: t('moreMenu.items.wallets'), icon: Wallet, route: "/wallet-management", color: "bg-indigo-500" },
        { id: "buy-accounts", label: t('moreMenu.items.buyAccounts'), icon: ShoppingCart, route: "/buy-accounts", color: "bg-rose-500" },
        { id: "cards", label: t('moreMenu.items.myCards'), icon: CreditCard, route: "/card-management", color: "bg-purple-500" },
        { id: "expense", label: t('moreMenu.items.expenseAnalytics'), icon: TrendingUp, route: "/analytics", color: "bg-teal-500" },
        { id: "devices", label: t('moreMenu.items.connectedDevices'), icon: Smartphone, route: "/trusted-devices", color: "bg-orange-500" },
      ]
    },
    {
      title: t('moreMenu.sections.settings'),
      items: [
        { id: "profile", label: t('moreMenu.items.profile'), icon: User, route: "/profile", color: "bg-slate-500" },
        { id: "kyc", label: t('moreMenu.items.kyc'), icon: Shield, route: "/kyc", color: "bg-emerald-500" },
        { id: "theme", label: t('moreMenu.items.theme'), icon: Palette, route: "/mobile-background-settings", color: "bg-violet-500" },
        { id: "notifications", label: t('moreMenu.items.notifications'), icon: Bell, route: "/notifications", color: "bg-red-500" },
      ]
    },
    {
      title: t('moreMenu.sections.others'),
      items: [
        ...(showInstallOption ? [{ id: "install", label: t('moreMenu.items.installApp'), icon: Download, action: handleInstallApp, color: "bg-primary" }] : []),
        { id: "settings", label: t('moreMenu.items.settings'), icon: Settings, route: "/settings", color: "bg-gray-500" },
        { id: "help", label: t('moreMenu.items.help'), icon: HelpCircle, route: "/help-center", color: "bg-cyan-500" },
        { id: "logout", label: t('moreMenu.items.logout'), icon: LogOut, action: handleLogout, color: "bg-red-600" },
      ]
    }
  ];

  const handleItemClick = (item: MenuItem) => {
    if (item.action) {
      item.action();
    } else if (item.route) {
      navigate(item.route);
      onClose();
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="bottom" className="h-[85vh] rounded-t-3xl">
        <SheetHeader className="pb-4">
          <SheetTitle className="text-xl font-bold">{t('moreMenu.title')}</SheetTitle>
        </SheetHeader>
        
        <div className="overflow-y-auto h-full pb-20 space-y-6">
          {menuSections.map((section) => (
            <div key={section.title}>
              <h3 className="text-sm font-semibold text-muted-foreground mb-3 px-1">
                {section.title}
              </h3>
              <div className="grid grid-cols-4 gap-3">
                {section.items.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleItemClick(item)}
                    className={cn(
                      "relative flex flex-col items-center justify-center p-3 rounded-2xl",
                      "bg-card hover:bg-muted/50 transition-all duration-200",
                      "active:scale-95 border border-border/50"
                    )}
                  >
                    <div className={cn(
                      "relative w-12 h-12 rounded-2xl flex items-center justify-center mb-2",
                      item.color
                    )}>
                      <item.icon className="h-6 w-6 text-white" />
                      {item.badge && item.badge > 0 && (
                        <Badge 
                          variant="destructive" 
                          className="absolute -top-1 -right-1 h-5 min-w-5 px-1 text-[10px] font-bold flex items-center justify-center"
                        >
                          {item.badge > 99 ? '99+' : item.badge}
                        </Badge>
                      )}
                    </div>
                    <span className="text-xs font-medium text-center leading-tight">
                      {item.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
